/**
 * Unique vertical scroll owner for a form shell (Swing Form/Page JScrollPane).
 * Table leftover (`--fill`) still wins in CSS via :has(.modern-table--fill).
 */

export type FormScrollOwner = 'table' | 'form' | 'page' | 'main-body'

type TextLikeNode = {
  _text?: unknown
  value?: unknown
}

function readNodeText(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'boolean' || typeof value === 'number') return String(value)
  if (value && typeof value === 'object') {
    const node = value as TextLikeNode
    if (typeof node._text === 'string' || typeof node._text === 'boolean' || typeof node._text === 'number') {
      return String(node._text)
    }
    if (typeof node.value === 'string' || typeof node.value === 'boolean' || typeof node.value === 'number') {
      return String(node.value)
    }
  }
  return ''
}

function isTrueFlag(value: unknown): boolean {
  const text = readNodeText(value).trim().toLowerCase()
  return text === 'true' || text === '1'
}

function readGrabVerticalFromRecord(node: Record<string, unknown>): boolean {
  return isTrueFlag(node.grabVertical)
}

/** Parsed compact XML: `{ grabVertical: { _text } }` on form or nested `form.form`. */
export function isFormGrabVertical(form: unknown): boolean {
  if (!form || typeof form !== 'object') return false
  const root = form as Record<string, unknown>
  if (readGrabVerticalFromRecord(root)) return true
  const inner = root.form
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return readGrabVerticalFromRecord(inner as Record<string, unknown>)
  }
  return false
}

export type ResolveFormScrollOwnerInput = {
  formGrabVertical?: boolean
  /** When known (tests). Runtime leftover tables also clip via CSS :has(--fill). */
  hasFillTable?: boolean
  dialogSized?: boolean
  inDialog?: boolean
}

/**
 * Swing:
 * - fill leftover table → table body JScrollPane
 * - form grabVertical → JScrollPane around the form
 * - sized dialog without fill → leftover main body
 * - else → page JScrollPane (header stays NORTH)
 */
export function resolveFormScrollOwner(input: ResolveFormScrollOwnerInput = {}): FormScrollOwner {
  if (input.hasFillTable) return 'table'
  if (input.formGrabVertical) return 'form'
  if (input.dialogSized) return 'main-body'
  if (input.inDialog) return 'form'
  return 'page'
}
